class FcFaq extends HTMLElement {
  constructor() {
    super();

    this.items = [];
    this.allowMultiple = false;
    this.animationDuration = 380;
    this.resizeObserver = null;

    this.reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.allowMultiple = this.dataset.allowMultiple === 'true';

    const configuredDuration = Number.parseInt(
      this.dataset.animationDuration,
      10
    );

    if (!Number.isNaN(configuredDuration)) {
      this.animationDuration = configuredDuration;
    }

    this.style.setProperty(
      '--fc-faq-animation-duration',
      `${this.animationDuration}ms`
    );

    this.applySettings();
    this.refreshItems();
    this.normalizeInitialState();
    this.bindEvents();
    this.observeAnswerChanges();
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.disconnectResizeObserver();
    this.cancelAllTransitions();

    delete this.dataset.initialized;
  }

  applySettings() {
    const pixelSettings = {
      '--fc-faq-item-gap': this.dataset.itemGap,
      '--fc-faq-column-gap': this.dataset.columnGap,
      '--fc-faq-image-radius': this.dataset.imageRadius,
      '--fc-faq-sticky-top': this.dataset.stickyTop,
      '--fc-faq-item-radius': this.dataset.itemRadius,
      '--fc-faq-item-border-width': this.dataset.itemBorderWidth,
      '--fc-faq-icon-size': this.dataset.iconSize,
      '--fc-faq-padding-top': this.dataset.paddingTop,
      '--fc-faq-padding-bottom': this.dataset.paddingBottom
    };

    Object.entries(pixelSettings).forEach(([property, value]) => {
      const numericValue = Number.parseFloat(value);

      if (!Number.isNaN(numericValue)) {
        this.style.setProperty(property, `${numericValue}px`);
      }
    });

    const imageWidth = Number.parseFloat(this.dataset.imageWidth);

    if (!Number.isNaN(imageWidth)) {
      this.style.setProperty(
        '--fc-faq-image-width',
        `${imageWidth}%`
      );
    }

    const colorSettings = {
      '--fc-faq-inactive-background':
        this.dataset.inactiveBackground,
      '--fc-faq-inactive-text':
        this.dataset.inactiveText,
      '--fc-faq-inactive-border':
        this.dataset.inactiveBorder,
      '--fc-faq-active-background':
        this.dataset.activeBackground,
      '--fc-faq-active-text':
        this.dataset.activeText,
      '--fc-faq-active-border':
        this.dataset.activeBorder,
      '--fc-faq-answer-background':
        this.dataset.answerBackground,
      '--fc-faq-answer-text':
        this.dataset.answerText,
      '--fc-faq-icon-background':
        this.dataset.iconBackground,
      '--fc-faq-icon-color':
        this.dataset.iconColor,
      '--fc-faq-active-icon-background':
        this.dataset.activeIconBackground,
      '--fc-faq-active-icon-color':
        this.dataset.activeIconColor
    };

    Object.entries(colorSettings).forEach(([property, value]) => {
      if (value) {
        this.style.setProperty(property, value);
      }
    });
  }

  bindEvents() {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);

    window.addEventListener(
      'resize',
      this.handleResize,
      { passive: true }
    );

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener(
        'shopify:block:select',
        this.handleBlockSelect
      );
    }
  }

  unbindEvents() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleResize);

    document.removeEventListener(
      'shopify:block:select',
      this.handleBlockSelect
    );
  }

  refreshItems() {
    this.items = Array.from(
      this.querySelectorAll('[data-fc-faq-item]')
    )
      .map((item) => ({
        item,
        trigger: item.querySelector('[data-fc-faq-trigger]'),
        panel: item.querySelector('[data-fc-faq-panel]'),
        panelInner: item.querySelector('[data-fc-faq-panel-inner]'),
        blockId: item.dataset.blockId || '',
        transitionEndHandler: null
      }))
      .filter((entry) => {
        return entry.trigger && entry.panel && entry.panelInner;
      });
  }

  normalizeInitialState() {
    const openItems = this.items.filter((entry) => {
      return entry.trigger.getAttribute('aria-expanded') === 'true';
    });

    if (!this.allowMultiple && openItems.length > 1) {
      openItems.slice(1).forEach((entry) => {
        this.setClosedState(entry);
      });
    }

    this.items.forEach((entry) => {
      const isOpen =
        entry.trigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        this.setOpenState(entry);
      } else {
        this.setClosedState(entry);
      }
    });
  }

  observeAnswerChanges() {
    if (!('ResizeObserver' in window)) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((observedEntry) => {
        const entry = this.items.find((item) => {
          return item.panelInner === observedEntry.target;
        });

        if (!entry) return;

        const isOpen =
          entry.trigger.getAttribute('aria-expanded') === 'true';

        if (!isOpen) return;

        if (
          entry.panel.classList.contains(
            'fc-faq__panel--animating'
          )
        ) {
          entry.panel.style.height =
            `${entry.panelInner.scrollHeight}px`;
        } else {
          entry.panel.style.height = 'auto';
        }
      });
    });

    this.items.forEach((entry) => {
      this.resizeObserver.observe(entry.panelInner);
    });
  }

  disconnectResizeObserver() {
    if (!this.resizeObserver) return;

    this.resizeObserver.disconnect();
    this.resizeObserver = null;
  }

  handleClick(event) {
    const trigger = event.target.closest(
      '[data-fc-faq-trigger]'
    );

    if (!trigger || !this.contains(trigger)) return;

    const entry = this.items.find((item) => {
      return item.trigger === trigger;
    });

    if (entry) {
      this.toggleItem(entry);
    }
  }

  handleKeydown(event) {
    const trigger = event.target.closest(
      '[data-fc-faq-trigger]'
    );

    if (!trigger || !this.contains(trigger)) return;

    const currentIndex = this.items.findIndex((entry) => {
      return entry.trigger === trigger;
    });

    if (currentIndex < 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusItem(currentIndex + 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusItem(currentIndex - 1);
        break;

      case 'Home':
        event.preventDefault();
        this.focusItem(0);
        break;

      case 'End':
        event.preventDefault();
        this.focusItem(this.items.length - 1);
        break;

      case 'Escape':
        event.preventDefault();
        this.closeItem(this.items[currentIndex]);
        trigger.focus();
        break;

      default:
        break;
    }
  }

  handleBlockSelect(event) {
    const selectedBlock = event.target;

    if (!selectedBlock || !this.contains(selectedBlock)) return;

    this.disconnectResizeObserver();
    this.refreshItems();
    this.observeAnswerChanges();

    const selectedEntry = this.items.find((entry) => {
      return entry.blockId === selectedBlock.dataset.blockId;
    });

    if (!selectedEntry) return;

    if (!this.allowMultiple) {
      this.closeOtherItems(selectedEntry);
    }

    this.openItem(selectedEntry);

    window.requestAnimationFrame(() => {
      selectedEntry.item.scrollIntoView({
        behavior: this.prefersReducedMotion()
          ? 'auto'
          : 'smooth',
        block: 'center'
      });
    });
  }

  handleResize() {
    this.items.forEach((entry) => {
      const isOpen =
        entry.trigger.getAttribute('aria-expanded') === 'true';

      if (!isOpen) return;

      if (
        entry.panel.classList.contains(
          'fc-faq__panel--animating'
        )
      ) {
        entry.panel.style.height =
          `${entry.panelInner.scrollHeight}px`;
      } else {
        entry.panel.style.height = 'auto';
      }
    });
  }

  toggleItem(entry) {
    const isOpen =
      entry.trigger.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closeItem(entry);
      return;
    }

    if (!this.allowMultiple) {
      this.closeOtherItems(entry);
    }

    this.openItem(entry);
  }

  openItem(entry) {
    if (
      !entry?.item ||
      !entry?.trigger ||
      !entry?.panel
    ) {
      return;
    }

    this.clearTransitionHandler(entry);

    entry.trigger.setAttribute('aria-expanded', 'true');
    entry.item.classList.add('fc-faq__item--active');
    entry.panel.hidden = false;

    if (this.prefersReducedMotion()) {
      this.setOpenState(entry);
      return;
    }

    entry.panel.classList.remove('fc-faq__panel--open');
    entry.panel.classList.add('fc-faq__panel--animating');

    entry.panel.style.height = '0px';
    entry.panel.style.opacity = '0';
    entry.panel.style.visibility = 'visible';

    entry.panel.getBoundingClientRect();

    const targetHeight = entry.panelInner.scrollHeight;

    window.requestAnimationFrame(() => {
      entry.panel.style.height = `${targetHeight}px`;
      entry.panel.style.opacity = '1';
    });

    entry.transitionEndHandler = (event) => {
      if (
        event.target !== entry.panel ||
        event.propertyName !== 'height'
      ) {
        return;
      }

      this.clearTransitionHandler(entry);

      if (
        entry.trigger.getAttribute('aria-expanded') === 'true'
      ) {
        this.setOpenState(entry);
      }
    };

    entry.panel.addEventListener(
      'transitionend',
      entry.transitionEndHandler
    );
  }

  closeItem(entry) {
    if (
      !entry?.item ||
      !entry?.trigger ||
      !entry?.panel
    ) {
      return;
    }

    this.clearTransitionHandler(entry);

    entry.trigger.setAttribute('aria-expanded', 'false');
    entry.item.classList.remove('fc-faq__item--active');

    if (this.prefersReducedMotion()) {
      this.setClosedState(entry);
      return;
    }

    entry.panel.hidden = false;
    entry.panel.classList.remove('fc-faq__panel--open');
    entry.panel.classList.add('fc-faq__panel--animating');

    const currentHeight =
      entry.panel.getBoundingClientRect().height;

    entry.panel.style.height = `${currentHeight}px`;
    entry.panel.style.opacity = '1';
    entry.panel.style.visibility = 'visible';

    entry.panel.getBoundingClientRect();

    window.requestAnimationFrame(() => {
      entry.panel.style.height = '0px';
      entry.panel.style.opacity = '0';
    });

    entry.transitionEndHandler = (event) => {
      if (
        event.target !== entry.panel ||
        event.propertyName !== 'height'
      ) {
        return;
      }

      this.clearTransitionHandler(entry);

      if (
        entry.trigger.getAttribute('aria-expanded') === 'false'
      ) {
        this.setClosedState(entry);
      }
    };

    entry.panel.addEventListener(
      'transitionend',
      entry.transitionEndHandler
    );
  }

  setOpenState(entry) {
    this.clearTransitionHandler(entry);

    entry.trigger.setAttribute('aria-expanded', 'true');
    entry.item.classList.add('fc-faq__item--active');

    entry.panel.hidden = false;
    entry.panel.classList.remove('fc-faq__panel--animating');
    entry.panel.classList.add('fc-faq__panel--open');

    entry.panel.style.height = 'auto';
    entry.panel.style.opacity = '1';
    entry.panel.style.visibility = 'visible';
  }

  setClosedState(entry) {
    this.clearTransitionHandler(entry);

    entry.trigger.setAttribute('aria-expanded', 'false');
    entry.item.classList.remove('fc-faq__item--active');

    entry.panel.classList.remove(
      'fc-faq__panel--open',
      'fc-faq__panel--animating'
    );

    entry.panel.style.height = '0px';
    entry.panel.style.opacity = '0';
    entry.panel.style.visibility = 'hidden';
    entry.panel.hidden = true;
  }

  closeOtherItems(activeEntry) {
    this.items.forEach((entry) => {
      if (entry === activeEntry) return;

      const isOpen =
        entry.trigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        this.closeItem(entry);
      }
    });
  }

  clearTransitionHandler(entry) {
    if (!entry.transitionEndHandler) return;

    entry.panel.removeEventListener(
      'transitionend',
      entry.transitionEndHandler
    );

    entry.transitionEndHandler = null;
  }

  cancelAllTransitions() {
    this.items.forEach((entry) => {
      this.clearTransitionHandler(entry);
    });
  }

  focusItem(index) {
    if (this.items.length === 0) return;

    let targetIndex = index;

    if (targetIndex < 0) {
      targetIndex = this.items.length - 1;
    }

    if (targetIndex >= this.items.length) {
      targetIndex = 0;
    }

    this.items[targetIndex]?.trigger?.focus();
  }

  prefersReducedMotion() {
    return this.reducedMotionQuery.matches;
  }
}

if (!customElements.get('fc-faq')) {
  customElements.define('fc-faq', FcFaq);
}